import React, { useState, useMemo } from "react";
import { Button } from "react-bootstrap";
import fonts_config from "./fonts_config.json";
import moment from "moment-timezone";
import pdfMake from "pdfmake/build/pdfmake.js";
import pdfFonts from "pdfmake/build/vfs_fonts.js";

import { useFormatter } from "./../../../util/formatter";
import { useSolarCalc } from "../../../util/calculations";
import { useUser } from "../../../util/user";
import usePeakLabels from "../../../util/peakLabels";

import LoaderGraph from "../../general/LoaderGraph";
import GYLogo from "../../../images/BASE64/GYLogo";
import accSignature from "../../../images/BASE64/accSignature";
import { buildInvoiceReceiptNumber } from "../../../util/invoice";
import rfdc from 'rfdc';
import GYAxios from "../../../util/GYAxios";

const clone = rfdc();

pdfMake.vfs = pdfFonts.pdfMake ? pdfFonts.pdfMake.vfs : pdfMake.vfs;
pdfMake.fonts = fonts_config;

function SummaryInvoiceCFW({ invoiceCopyType = 'ORIGINAL', onDocDefinition = () => { }, docType, ...props }) {
	const [formatNumber] = useFormatter();
	const user = useUser();
	const solarCalc = useSolarCalc();
	const peakLabels = usePeakLabels("TH");

	const [PDF, setPDF] = useState(null);
	const [PDFDoc, setPDFDoc] = useState(null);
	const phaseCode = props.selectedPhase;
	const selectedMonth = props.selectedMonth;
	const selectedMonthStr = selectedMonth.format("YYYY-MM-DD");

	const copyTypes = useMemo(() => ({
		ORIGINAL: "Original (ต้นฉบับ)",
		COPY: 'Copy (สำเนา)'
	}), []);

	const selectedMonthCode = moment(selectedMonthStr, "YYYY-MM-DD").format(
		"YYMM"
	);
	const selectedMonthToDate = moment(selectedMonthStr, "YYYY-MM-DD").endOf(
		"month"
	);
	const invNo = buildInvoiceReceiptNumber({ type: 'INV', solarEntityId: 'S1', siteCode: `CFW${phaseCode}`, date: selectedMonthStr, });
	const period = `Period : ${moment(selectedMonthStr, "YYYY-MM-DD").format(
		"MMMM YYYY"
	)}`;
	const dueDate = moment(selectedMonthStr, "YYYY-MM-DD").endOf("month").add(30, "days");

	const logo = GYLogo;
	const signImg = accSignature;

	const signObj = {
		image: signImg,
		height: 50,
		width: 107,
		absolutePosition: { x: -300, y: 120 },
	};
	const showSignature = user.roles.includes(
		"administration:InvoiceTable:_clientViewInv"
	);

	const signature = showSignature ? signObj : {};

	// Table layout config
	const getTableLayoutConfig = () => ({
		defaultBorder: false,
		paddingLeft: function (i, node) {
			return 0;
		},
		paddingRight: function (i, node) {
			return 0;
		},
		paddingTop: function (i, node) {
			return 0;
		},
		paddingBottom: function (i, node) {
			return 0;
		},
	});

	const groupSitesByOperationStatus = (sites) => {
		const sitesGrouped = sites.reduce((acc, site) => {
			const status = site.operation_status;

			if (!acc[status]) {
				acc[status] = [];
			}
			acc[status].push(site);
			return acc;
		}, {});
		// return as an array
		return Object.values(sitesGrouped);
	};

	const groupSitesByNumber = (sitesArr, n) => {
		const sitesGrouped = sitesArr.reduce((acc, site, i) => {
			const index = Math.floor(i / n);
			if (!acc[index]) {
				acc[index] = [];
			}
			acc[index].push(site);
			return acc;
		}, []);
		// return as an array
		return sitesGrouped;
	};

	/**
	 * Calculation of each site
	 * @param {Site} site
	 */
	const calculations = (site) => {
		const ON_unitCost = solarCalc.calculateUnitCost(
			site.price_on,
			site.ON_discount,
			site.currency
		);
		const OFF_unitCost = solarCalc.calculateUnitCost(
			site.price_off,
			site.OFF_discount,
			site.currency
		);
		const FT_unitCost = solarCalc.calculateUnitCost(
			site.FT_cost,
			site.FT_discount,
			site.currency
		);

		const prod = {
			ON: site.solar_ON,
			OFF: site.solar_OFF,
			TOTAL: site.solar_total,
		};
		const price = { ON: site.price_on, OFF: site.price_off, FT: site.FT_cost };
		const discount = {
			ON: site.ON_discount,
			OFF: site.OFF_discount,
			FT: site.FT_discount,
		};
		const savingsObj = solarCalc.calculateSavings(
			prod,
			price,
			discount,
			site.currency
		);

		const costObj = solarCalc.calculateProdCost(
			prod,
			price,
			discount,
			site.currency
		);
		const solar_ONsub = costObj.costON;
		const solar_OFFsub = costObj.costOFF;
		const solar_totalsub = costObj.costFT;
		const subtotal = costObj.costTotal;
		const savings = savingsObj.savingsTotal;

		const currency = site.currency;
		const vatPercent = site.vat;

		const VATObj = solarCalc.calculateVAT(subtotal, currency, vatPercent);
		const subVAT = VATObj.VAT;

		const formattedCalc = {
			ON_unitCost,
			OFF_unitCost,
			FT_unitCost,
			solar_ONsub,
			solar_OFFsub,
			solar_totalsub,
			subVAT,
			subtotal,
			vatPercent,
			savings
		};
		return formattedCalc;
	};

	/**
	 * Prepare site array for PDFMAKE config
	 * @param {Sites array} sitesArr
	 */
	const formatSitesArr = (sitesArr) => {
		let overallSubtotal = 0;
		
		const sites = sitesArr.map((siteGroup) => {
			const resGroup = siteGroup.map((site) => {

				const calc = calculations(site);
				const siteName = `${site.company} ${site.site_name}`;
				/* const siteCode = `BD-CFW-${selectedMonthCode}-01`; */
				const siteCode = `INV-${site.entity_id}-${site.site_code}-${selectedMonthCode}-01`;

				const firstDay = moment(site.dateData).format("DD/MM/YYYY");
				const lastDay = selectedMonthToDate.format("DD/MM/YYYY");

				const period = `(${firstDay} - ${lastDay})`;
				const siteHeader = `${siteName} - ${siteCode}`;

				overallSubtotal += solarCalc.parseFormatNumber(calc.subtotal, 2);

				return {
					siteHeader,
					period: `Period: ${period}`,
					ON_discount: `Electricity Producton during ${peakLabels[0]} Time - Discount ${parseFloat((site.ON_discount * 100).toFixed(2))}%`,
					OFF_discount: `Electricity Producton during ${peakLabels[1]} Time - Discount ${parseFloat((site.OFF_discount * 100).toFixed(2))}%`,
					FT_discount: `Ft Cost - Discount ${parseFloat((site.FT_discount * 100).toFixed(2))}%`,
					ON_unitCost: calc.ON_unitCost.toFixed(6),
					OFF_unitCost: calc.OFF_unitCost.toFixed(6),
					FT_unitCost: calc.FT_unitCost.toFixed(6),
					solar_ON: formatNumber(site.solar_ON, 3),
					solar_OFF: formatNumber(site.solar_OFF, 3),
					solar_total: formatNumber(site.solar_total, 3),
					solar_ONsub: formatNumber(calc.solar_ONsub, 2),
					solar_OFFsub: formatNumber(calc.solar_OFFsub, 2),
					solar_totalsub: formatNumber(calc.solar_totalsub, 2),
					subtotal: formatNumber(calc.subtotal, 2),
					vat: calc.vatPercent,
					savings: `Savings: ${parseFloat(calc.savings.toFixed(2))} ${site.currency}`,
				};
			});
			return resGroup;
		});
		const vatPercent = sitesArr[0][0].vat;
		const currency = sitesArr[0][0].currency;

		const VATObj = solarCalc.calculateVAT(
			overallSubtotal,
			currency,
			vatPercent
		);

		const overallVAT = VATObj.VAT;
		const subtotal = solarCalc.parseFormatNumber(overallSubtotal, 2);
		const vat = solarCalc.parseFormatNumber(overallVAT, 2);
		const grandTotal = solarCalc.parseFormatNumber(subtotal + vat, 2);
		const overall = {
			subtotal,
			vat,
			grandTotal,
		};

		return { sites, overall };
	};

	/**
	 * Build table
	 * @param {Parameters} param
	 * @param {Is overall table} isOverall
	 */
	const getTable = (id, param, isOverall) => {
		let tableElements = [];

		// Sites rows
		if (!isOverall) {
			// Constructing table rows
			tableElements = [
				[
					{
						text: "DESCRIPTION",
						border: [false, true, false, true],
						bold: true,
						style: "table_header",
					},
					{
						border: [false, true, false, true],
						bold: true,
						text: "QUANTITY (kWh)",
						alignment: "right",
						style: "table_header",
					},
					{
						border: [false, true, false, true],
						bold: true,
						text: "UNIT COST",
						alignment: "right",
						style: "table_header",
					},
					{
						border: [false, true, false, true],
						bold: true,
						text: "TOTAL",
						alignment: "right",
						style: "table_header",
					},
				],
			];

			// Period display on first page only
			if (id === 0) {
				/* tableElements.push([
					{
						text: period,
						bold: true,
						style: "table_header",
						colSpan: 4,
					},
				]); */
			}

			// Building table rows
			param.map((site) => {
				tableElements.push(
					[
						{
							text: site.siteHeader,
							bold: true,
							style: "table_header",
							colSpan: 4,
						},
					],
					[
						{
							text: site.period,
							style: "table_main_rows_summary_inv",
							colSpan: 4,
						},
					],
					[
						{
							text: site.ON_discount,
							style: "table_main_rows_summary_inv",
						},
						{
							text: site.solar_ON,
							alignment: "right",
							style: "table_main_rows_summary_inv",
						},
						{
							text: site.ON_unitCost,
							alignment: "right",
							style: "table_main_rows_summary_inv",
						},
						{
							bold: true,
							text: site.solar_ONsub,
							alignment: "right",
							style: "table_main_rows_summary_inv",
						},
					],
					[
						{
							text: site.OFF_discount,
							style: "table_main_rows_summary_inv",
						},
						{
							text: site.solar_OFF,
							alignment: "right",
							style: "table_main_rows_summary_inv",
						},
						{
							text: site.OFF_unitCost,
							alignment: "right",
							style: "table_main_rows_summary_inv",
						},
						{
							bold: true,
							text: site.solar_OFFsub,
							alignment: "right",
							style: "table_main_rows_summary_inv",
						},
					],
					[
						{
							text: site.FT_discount,
							style: "table_main_rows_summary_inv",
						},
						{
							text: site.solar_total,
							alignment: "right",
							style: "table_main_rows_summary_inv",
						},
						{
							text: site.FT_unitCost,
							alignment: "right",
							style: "table_main_rows_summary_inv",
						},
						{
							bold: true,
							text: site.solar_totalsub,
							alignment: "right",
							style: "table_main_rows_summary_inv",
						},
					],
					[
						{
							text: site.savings,
							style: "savings",
							colSpan: 4,
						},
					],
					[
						{
							bold: true,
							alignment: "right",
							text: "Subtotal",
							style: "table_main_rows_summary_inv",
							colSpan: 3,
						},
						"",
						"",
						{
							bold: true,
							text: site.subtotal,
							alignment: "right",
							style: "table_main_rows_summary_inv",
							border: [false, false, false, true],
						},
					]
				);
			});
		}
		// Overall (Grand Total) on the last page
		else {
			tableElements = [
				[
					{
						bold: true,
						alignment: "right",
						text: "Subtotal",
						style: "table_main_rows_summary_inv",
						colSpan: 3,
					},
					"",
					"",
					{
						bold: true,
						text: formatNumber(param.subtotal, 2),
						alignment: "right",
						style: "table_main_rows_summary_inv",
					},
				],
				[
					{
						bold: true,
						alignment: "right",
						text: "VAT 7%",
						style: "table_main_rows_summary_inv",
						colSpan: 3,
					},
					"",
					"",
					{
						bold: true,
						text: formatNumber(param.vat, 2),
						alignment: "right",
						style: "table_main_rows_summary_inv",
						border: [false, false, false, true],
					},
				],
				[
					{
						bold: true,
						alignment: "right",
						text: "TOTAL DUE",
						style: "table_main_rows_summary_inv",
						colSpan: 3,

						border: [false, true, false, true],
					},
					"",
					"",
					{
						bold: true,
						text: formatNumber(param.grandTotal, 2),
						alignment: "right",
						style: "table_main_rows_summary_inv",
						border: [false, true, false, true],
					},
				],
			];
		}
		return tableElements;
	};

	/**
	 * Build payment section object
	 * @param {Bank account obj} bankAccount
	 * @returns Payment section obj
	 */
	const buildPaymentSection = async (phaseCode, op_status) => {
		let _bankAccount = {};
		if (phaseCode == "S4") {
			_bankAccount = (await GYAxios.get(`/select/banks?acc_number=049-4-51624-9`)).data;
		} else if (phaseCode == "GYS6" && op_status == 2) {
			_bankAccount = (await GYAxios.get(`/select/banks?acc_number=049-445516-8`)).data;
		} else if (phaseCode == "GYS6" && op_status == 3) {
			_bankAccount = (await GYAxios.get(`/select/banks?acc_number=433-173120-5`)).data;
		}
		
		if (_bankAccount.length === 0) {
			return {
				_bankAccount: {},
				section: [],
			};
		} else {
			const bankAccount = _bankAccount[0];
			const accNumber = bankAccount.accNumber;
			const accNumRemark = bankAccount.accNumRemark;

			let accountNumbers = accNumber != null ? accNumber.split(",") : ["-"];
			let accountRemarks = accNumRemark != null ? accNumRemark.split(",") : ["-"];

			const accName = bankAccount.accName != null ? bankAccount.accName : "-";
			const bankName = bankAccount.bankName != null ? bankAccount.bankName : "-";
			const branch = bankAccount.branch != null ? bankAccount.branch : "-";
			const swiftCode =
				bankAccount.swiftCode != null ? bankAccount.swiftCode : "-";

			let accNumSection = [];

			if (accountNumbers.length === accountRemarks.length) {
				accountNumbers = accountNumbers.map((str) => str.trim());
				accountRemarks = accountRemarks.map((str) => str.trim());

				accNumSection.push("Beneficiary Account number : ");

				for (let i = 0; i < accountNumbers.length; i++) {
					const accNumberStr = { text: accountNumbers[i], style: "boldHeader" };
					const accRemarkStr = { text: ` [${accountRemarks[i]}]  ` };

					accNumSection.push(accNumberStr);
					accNumSection.push(accRemarkStr);
				}
			}

			const section = [
				{
					text: [
						"Please make the payment to ",
						{
							text: accName,
							style: "boldHeader",
						},
					],
				},
				{
					text: [
						"Bank : ",
						{ text: `${bankName}, `, style: "boldHeader" },
						"Branch : ",
						{ text: branch, style: "boldHeader" },
					],
				},
				{
					text: accNumSection,
				},
				{
					text: ["Bank Swift Code :", { text: swiftCode, style: "boldHeader" }],
				},
			];

			return section;
		}
	};

	const buildEntitySection = async (phaseCode, op_status) => {
		let solarEntity = {};
		if (phaseCode == "S4") {
			solarEntity = (await GYAxios.get(`/select/entityInfo/S4`)).data;
		} else if (phaseCode == "GYS6" && op_status == 2) {
			solarEntity = (await GYAxios.get(`/select/entityInfo/TH`)).data;
		} else if (phaseCode == "GYS6" && op_status == 3) {
			solarEntity = (await GYAxios.get(`/select/entityInfo/GYS6`)).data;
		}

		// Check if entity is empty or an object
		if (solarEntity.length === 0) {
			return {
				solarEntity: {},
				section: [],
			};
		} else {
			const _solarEntity = solarEntity[0];
			const addressArr = _solarEntity.address.split("_");
			const addressRows = addressArr.map((rows) => ({ text: rows, style: "addressHeader", }));

			const section = [
				{
					text: _solarEntity.entity_name,
					bold: true,
					style: "addressHeader",
				},
				...addressRows,
				{
					text: `TAX ID : ${_solarEntity.tax_id}`, style: "addressHeader",
				},
				{
					text: `Contact person : ${_solarEntity.contact_name}`, style: "addressHeader",
				},
				{
					text: `Tel: ${_solarEntity.telephone}`, sstyle: ["addressHeader", { bold: true }],
				},
			];
			return { solarEntity: _solarEntity, section };
		}
	};

	const getHeader = (entity, customer, bdNo) => ({
		columns: [
			{
				nodeName: "DIV",
				width: "50%",
				stack: [
					{
						image: logo,
						width: 150,
					},
					...entity, // entity information (which is an array)
					{
						text: "TO",
						style: ["addressHeader", { bold: true }],
					},
					{
						nodeName: "DIV",
						margin: [20, 10],
						stack: [
							{
								text: customer.name,
								style: ["addressHeader", { bold: true }],
							},
							{
								text: customer.address,
								style: "addressHeader",
							},
							{
								text: `TAX ID: ${customer.tax_id}`,
								style: "addressHeader",
							},
						],
					},
				],
			},
			{
				width: "50%",
				stack: [
					// second column consists of paragraphs
					{ text: "Summary billing detail", style: "header_lg" },
					{ text: `ใบสรุปรวม`, style: "sub_header_2", margin: 0 },
					{
						margin: [30, 0, 0, 0],
						table: {
							body: [
								[
									{
										text: "Billing Detail No.",
										bold: true,
									},
									{
										text: bdNo,
									},
								],
								[
									{
										text: "DATE",
										bold: true,
									},
									{
										text: selectedMonthToDate.format("DD/MM/YYYY"),
									},
								],
								[
									{
										text: "CUSTOMER ID",
										bold: true,
									},
									{ text: "Central Food Wholesales" },
								],
								[
									{
										text: "Credit Term",
										bold: true,
									},
									{
										text: "30 days",
									},
								],
								[
									{
										text: "Due date",
										bold: true,
									},
									{
										text: dueDate.format("DD/MM/YYYY"),
									},
								],
							],
						},
						widths: ["45%", "55%"],
						layout: {
							defaultBorder: false,
						},
					},
				],
			},
		],
		margin: [0, 10, 0, 0],
	});

	/* const getHeader = async (headerInfo) => ({
		columns: [
			{
				nodeName: "DIV",
				width: "50%",
				stack: [
					{
						image: logo,
						width: 150,
					},
					{
						text: "Green Yellow Solar1 (Thailand) Co., LTD. (Head Office)",
						bold: true,
						style: "addressHeader",
					},
					{
						text: "48/43-44, TISCO Tower, 19th Floor,",
						style: "addressHeader",
					},
					{
						text: "North Sathorn Road, Silom Sub-district,",
						style: "addressHeader",
					},
					{
						text: "Bangrak District, Bangkok 10500",
						style: "addressHeader",
					},
					{ text: "TAX ID : 0105560061131", style: "addressHeader" },
					{
						text: "Contact person : Khanatpat Kamnoi (Acct.)",
						style: "addressHeader",
					},
					{
						text: "Tel: +66 (0) 2-079-8081 Ext. 201",
						style: "addressHeader",
					},
					{
						text: "TO",
						style: ["addressHeader", { bold: true }],
					},
					{
						nodeName: "DIV",
						margin: [20, 10],
						stack: [
							{
								text: headerInfo.billing_name,
								style: ["addressHeader", { bold: true }],
							},
							{
								text: headerInfo.billing_address,
								style: "addressHeader",
							},
							{
								text: "TAX ID: 0125565034662",
								style: "addressHeader",
							},
						],
					},
				],
			},
			{
				width: "50%",
				stack: [
					// second column consists of paragraphs
					{ text: "Invoice / Tax Invoice", style: "header_lg" },
					{ text: copyTypes[invoiceCopyType], style: "sub_header_2", margin: 0 },
					{
						margin: [30, 0, 0, 0],
						table: {
							body: [
								[
									{
										text: "INVOICE NO",
										bold: true,
									},
									{
										text: invNo,
									},
								],
								[
									{
										text: "DATE",
										bold: true,
									},
									{
										text: selectedMonthToDate.format("DD/MM/YYYY"),
									},
								],
								[
									{
										text: "CUSTOMER ID",
										bold: true,
									},
									{ text: "MAKRO" },
								],
								[
									{
										text: "Credit Term",
										bold: true,
									},
									{
										text: "30 days",
									},
								],
								[
									{
										text: "Due date",
										bold: true,
									},
									{
										text: dueDate.format("DD/MM/YYYY"),
									},
								],
							],
						},
						widths: ["45%", "55%"],
						layout: {
							defaultBorder: false,
						},
					},
				],
			},
		],
		margin: [0, 10, 0, 0],
	}); */

	const getFooter = (paymentSection, page = 1) => ({
		absolutePosition: { x: 0, y: 620 },
		stack: [
			{
				text: "*This invoice is exempt from income tax under BOI promotion and is therefore not subject to withholding tax.",
				color: "red",
				italic: true,
				margin: 10,
			},
			{
				columns: [
					{
						stack: [
							{
								text: "Validity of Invoice",
								style: {
									fontSize: 12,
									italics: true,
									bold: true,
								},
								margin: [10, 0, 0, 6],
							},
							{
								text: "If there is any dispute amount on this INVOICE for a specific site, such",
								margin: [10, 0, 0, 1],
							},
							{
								text: "dispute amount about the specific site shall not effect the total",
								margin: [10, 0, 0, 1],
							},
							{
								text: "payment of the INVOICE. Each site are considered seperately for",
								margin: [10, 0, 0, 1],
							},
							{
								text: "payment. Purpose, The customer acknowledge that they shall pay the",
								margin: [10, 0, 0, 1],
							},
							{
								text: "total amount with a good faith and shall not hold any payment for a",
								margin: [10, 0, 0, 1],
							},
							{
								text: "disagreement over a specific site.",
								margin: [10, 0, 0, 1],
							},
						],
					},
					{
						stack: [
							{
								text: "ความสมบูรณ์ของใบแจ้งหนี้",
								style: {
									fontSize: 12,
									italics: true,
									font: "Taviraj",
								},
								margin: [0, -6, 0, 0],
							},
							{
								text: "กรณียอดเงินที่ต้องชำระของไซต์งานใดไซต์หนึ่งในใบแจ้งหนี้ฉบับนี้ ต้อง",
								style: "thai_summary_inv",
							},
							{
								text: "ตกเป็นยอดเงินที่ยังตกลงกันไม่ได้ (Dispute amount) กรณีดังกล่าวนั้นย่อม",
								style: "thai_summary_inv",
							},
							{
								text: "ไม่กระทบกับยอดเงินที่ต้องชำระของไซต์งานอื่นๆ ซึ่งสามารถแยกชำระ",
								style: "thai_summary_inv",
							},
							{
								text: "ต่างหากจากกันได้ ทั้งนี้ผู้ซื้อทราบดีว่าจะต้องชำระยอดเงินทั้งหมดในใบแจ้งหนี้ฉบับนี้",
								style: "thai_summary_inv",
							},
							{
								text: "แจ้งหนี้ฉบับนี้โดยสุจริตและไม่คงค้างยอดเงินที่ยังตกลงกันไม่ได้",
								style: "thai_summary_inv",
							},
						],
					},
				],
				columnGap: 10,
			},
			{
				stack: clone(paymentSection),
				margin: [10, 10, 0, 0],
			},
			{
				text: page.toString(),
				alignment: "right",
				style: "normalText",
				bold: true,
				margin: [0, 0, 30, 0],
			},
			{
				stack: [
					{
						text: [
							"This is a computer-generated Invoice. No signature is required"
						],
					},
				],
				margin: [0, 20, 0, 0],
				style: {
					alignment: "center",
				},
			},
		],
	});

	/**
	 * Generate PDF with config
	 * @param {Sites array} sitesArr
	 */
	const generateInvoice = async (sitesArr, bankAccount) => {
		// return an array of sites grouped by operation status
		const sitesGroup = groupSitesByOperationStatus(sitesArr);
		let contentBodies = [];

		const CFWBillingInfo = {
			name: `Central Food Wholesales Limited (Head office)`,
			address: `177, 6th Floor, Ramkhamhaeng Road, Hua Mak Subdistrict, Bangkapi District, Bangkok, 10240 Thailand`,
			tax_id: "0125565034662",
		}

		for (let siteGroupId=0; siteGroupId<sitesGroup.length; siteGroupId++) {
			const siteGroup = sitesGroup[siteGroupId];
			const sitesPages = groupSitesByNumber(siteGroup, 5);
			const formattedSitesobj = formatSitesArr(sitesPages);

			const formattedSitesGroup = formattedSitesobj.sites;
			const overallCalc = formattedSitesobj.overall;

			// this is the foreach loop for each site grouped by operation status
			// so, we need to have header and footer for each site group
			// we also need to think about the phase (PHASE GYS6 and PHASE S4)
			// each phase has different header and footer information for each site group
			let contentBody = [];

			const phaseCode = siteGroup[0].entity_id; // representative 
			const op_status = siteGroup[0].operation_status; // representative

			const { solarEntity: entityInfo, section: entitySection } = await buildEntitySection(phaseCode, op_status);

			const bdNo = `BD-${entityInfo?.entity_id}-CFW-${selectedMonthCode}-01`;

			const paymentSection = bankAccount != null ? await buildPaymentSection(phaseCode, op_status) : {};

			for (let i = 0; i < formattedSitesGroup.length; i++) {
				const arrayOfSitesFormatted = formattedSitesGroup[i];

				let pageContent = {
					nodeName: 'DIV',
					margin: [10, 10, 10, 10],
					pageBreak: i === formattedSitesGroup.length - 1 && siteGroupId === sitesGroup.length-1 ? undefined : 'after',
					stack: [
						getHeader(entitySection, CFWBillingInfo, bdNo),
						{
							style: "table_main",
							table: {
								body: getTable(i, arrayOfSitesFormatted, false),
								widths: ["55%", "15%", "15%", "15%"],
							},
							layout: clone(getTableLayoutConfig()),
						},
						getFooter(paymentSection, i + 1),
					]
				};
				if (i === formattedSitesGroup.length - 1) { // add total between table and footer at the end of the site group
					let overallTable = {
						table: {
							body: getTable(-1, overallCalc, true),
							widths: ["55%", "15%", "15%", "15%"],
						},
						margin: [0, 10, 0, 0],
						layout: clone(getTableLayoutConfig()),
					};
					pageContent.stack.splice(2, 0, overallTable);
				}
				contentBody.push(pageContent);
				
			}

			contentBodies.push(contentBody);
		}

		const docDefinition = {
			content: [{ nodeName: 'DIV', margin: 0, style: 'makroInvoiceStyle', stack: contentBodies }],
			pageMargins: [0, 0, 0, 0],
			styles: {
				header_lg: {
					fontSize: 27,
					margin: [0, 0, 0, 0],
				},
				sub_header: {
					decoration: "underline",
				},
				sub_header_2: {
					fontSize: 14,
					color: "#aaa",
					font: "Trirong",
					margin: [0, 0, 0, 0]
				},
				table_header: {
					margin: [0, 2, 0, 0],
					fontSize: 10,
				},
				table_main: {
					margin: [0, 5, 0, 0],
				},
				table_main_rows_summary_inv: {
					margin: [0, 1, 0, 0],
				},
				savings: {
					color: "green",
					bold: true,
				},
				signature: {
					alignment: "center",
				},
				sub_signature: {
					margin: [0, 5, 0, 0],
				},
				boldHeader: { bold: true },
				thai_summary_inv: {
					font: "Trirong",
					fontSize: 9,
					margin: [0, 0, 0, -3],
				},
				makroInvoiceStyle: {
					font: "Calibri",
					fontSize: 9,
				},
			},
			defaultStyle: {
				font: "Calibri",
				fontSize: 9,
			},
		};

		onDocDefinition({ docType, invoiceCopyType, selectedSite: props.summaryInvoiceSites, makroPhase: phaseCode, docDefinition: clone(docDefinition) });

		const pdfDocGenerator = pdfMake.createPdf(docDefinition);
		setPDFDoc(docDefinition);

		if (PDF == null) {
			pdfDocGenerator.getDataUrl((dataUrl) => {
				setPDF(dataUrl);
			});
		}


	};

	/**
	 * Download PDF with allocated name
	 */
	const downloadPDF = () =>
		pdfMake.createPdf(PDFDoc).download(`${invNo.trim()}.pdf`);

	useMemo(() => {
		const labelsAreReady = !peakLabels.includes("");
		if (labelsAreReady) {
			const codSites = props.summaryInvoiceSites.filter(site => moment(site.cod).isSameOrBefore(selectedMonthToDate));
			generateInvoice(codSites, props.bankAccount);
		}
	}, [peakLabels]);

	return (
		<>
			{PDF != null ? (
				<>
					<iframe style={{ width: "98%", height: "72vh" }} src={PDF} />
					<Button onClick={downloadPDF}>Download</Button>
				</>
			) : (
				<LoaderGraph style={{ height: "72vh" }} />
			)}
		</>
	);
}

export default SummaryInvoiceCFW; 